import { ReelFeed } from './ReelFeed';
import { type ViewerVideo } from './types';
import { ViewerShell } from './ViewerShell';

type Props = {
  open: boolean;
  videos: readonly ViewerVideo[];
  /** Where the feed opens. Changes take effect on the next open, not during. */
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  label?: string;
};

/**
 * Short vertical video, full screen, one clip at a time.
 *
 * The shape every visitor already knows from Reels, Shorts and TikTok — and
 * chosen for a delivery archive because that is what the material is: a
 * fifteen-second clip of a car being handed over, shot on a phone, held
 * vertically. Pouring that into a 16:9 player with a scrub bar would letterbox
 * it on three sides and make it look like a document.
 *
 * Almost nothing happens in this file. The dialog chrome is `ViewerShell`,
 * which it shares with the lightbox, and everything about the feed is
 * `ReelFeed` — which exists as a separate component precisely so that it
 * mounts and unmounts with the dialog, and can therefore read the opening
 * index once instead of tracking it.
 *
 * No counter in the shell: each slide carries its own, because in a feed the
 * position belongs to the clip you are looking at rather than to the window.
 */
export function ReelViewer({
  open,
  videos,
  index,
  onIndexChange,
  onClose,
  label = 'Delivery videos',
}: Props) {
  if (videos.length === 0) return null;

  return (
    <ViewerShell open={open} onClose={onClose} label={label} surface="reel">
      <ReelFeed videos={videos} initialIndex={index} onActiveChange={onIndexChange} />
    </ViewerShell>
  );
}
